import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { FeaturedToursCarousel } from '../components/tours/FeaturedToursCarousel';
import { HorizontalTourCard } from '../components/tours/HorizontalTourCard';
import { TrustBadges } from '../components/common/TrustBadges';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { CompactImageSlider } from '../components/ui/ImageSlider';
import { toursAPI, seedAPI, quickSearchesAPI, categoriesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';
import { formatPrice, getCategoryColor, getCategoryLabel, truncateText } from '../lib/utils';
import { 
  Search, Mountain, Waves, Utensils, Moon, 
  ArrowRight, Star, Shield, Clock, ChevronRight,
  MapPin, Users, Sparkles, TrendingUp, X, Check, Flame,
  Compass, Sun, Camera, Map, Anchor, TreePine, Bike, Car, Plane, Tent
} from 'lucide-react';

// Icon mapping for dynamic categories
const iconMap = {
  Mountain, Waves, Utensils, Moon, Compass, Sun, Camera, Map, 
  Anchor, TreePine, Bike, Car, Plane, Tent, Sparkles, Star
};

// Default search suggestions (fallback if none in database)
const defaultSearchSuggestions = [
  { query: 'Bio Bay', category: 'night', icon: Moon, description: 'Night Tours' },
  { query: 'Rainforest', category: 'adventure', icon: TreePine, description: 'Adventure Tours' },
  { query: 'Old San Juan', category: 'food', icon: Utensils, description: 'Food & Culture' },
  { query: 'Night Tours', category: 'night', icon: Moon, description: 'Night Tours' },
  { query: 'Snorkeling', category: 'marine', icon: Waves, description: 'Marine Tours' },
  { query: 'Zipline', category: 'adventure', icon: Mountain, description: 'Adventure Tours' },
  { query: 'Food Tours', category: 'food', icon: Utensils, description: 'Food & Culture' },
  { query: 'Catamaran', category: 'marine', icon: Anchor, description: 'Marine Tours' },
];

// Fallback categories (used if API fails)
const fallbackCategories = [
  { id: 'adventure', slug: 'adventure', name: 'Adventure', icon: 'Mountain', color: '#22c55e' },
  { id: 'marine', slug: 'marine', name: 'Marine', icon: 'Waves', color: '#06b6d4' },
  { id: 'food', slug: 'food', name: 'Food & Culture', icon: 'Utensils', color: '#f97316' },
  { id: 'night', slug: 'night', name: 'Night Tours', icon: 'Moon', color: '#8b5cf6' },
];

const features = [
  { icon: Shield, title: 'Best Price Guarantee', description: 'We\'ll match any lower price' },
  { icon: Clock, title: 'Free Cancellation', description: '24 hour refund policy' },
  { icon: Star, title: 'Verified Reviews', description: 'Real traveler feedback' },
];

// Compact Tour Card Component with Image Slider
const CompactTourCard = ({ tour }) => {
  // Get first 3 images for the card slider
  const cardImages = tour.images?.length > 0 
    ? tour.images.slice(0, 3) 
    : [tour.image_url];

  // Generate consistent random for popularity/urgency
  const getConsistentRandom = (seed, min, max) => {
    const hash = (tour.id + seed).split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return min + Math.abs(hash) % (max - min + 1);
  };

  const bookingsLast24h = getConsistentRandom('bookings', 3, 12);
  const spotsLeft = getConsistentRandom('spots', 2, 6);
  const isVeryPopular = tour.rating >= 4.7 || tour.reviews_count > 150;
  const isSellingFast = bookingsLast24h >= 8;
  
  // Use tour-specific settings (default to true if not set)
  const showPopular = tour.show_popular_badge !== false;
  const showSellingFast = tour.show_selling_fast_badge !== false;
  const showSpotsLeft = tour.show_spots_left !== false;
  const hasDiscount = tour.discount_percentage > 0;

  return (
    <Link to={`/tours/${tour.id}`} data-testid={`tour-card-${tour.id}`}>
      <Card className="tour-card group overflow-hidden bg-white border border-border/50 rounded-xl h-full hover:shadow-lg transition-shadow duration-300">
        {/* Image Slider - Taller aspect ratio */}
        <div className="relative aspect-[4/3] overflow-hidden">
          <CompactImageSlider images={cardImages} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          
          {/* Category Badge - respects tour setting */}
          {tour.show_category_badge !== false && (
            <Badge 
              className={`absolute top-2 left-2 ${getCategoryColor(tour.category)} border-0 text-xs px-2 py-0.5`}
            >
              {getCategoryLabel(tour.category).replace(' Tours', '')}
            </Badge>
          )}

          {/* Discount Badge (Priority over Popular) */}
          {hasDiscount ? (
            <div className="absolute top-2 right-2 flex items-center gap-1 bg-red-600 text-white px-2 py-0.5 rounded-full text-xs font-bold">
              <span>{tour.discount_percentage}% OFF</span>
            </div>
          ) : isVeryPopular && showPopular ? (
            <div className="absolute top-2 right-2 flex items-center gap-1 bg-orange-500 text-white px-2 py-0.5 rounded-full text-xs font-semibold">
              <Flame className="h-3 w-3" />
              <span>Popular</span>
            </div>
          ) : isSellingFast && showSellingFast ? (
            <div className="absolute top-2 right-2 flex items-center gap-1 bg-red-500 text-white px-2 py-0.5 rounded-full text-xs font-semibold">
              <TrendingUp className="h-3 w-3" />
              <span>Selling Fast</span>
            </div>
          ) : null}
          
          {/* Price Badge with Discount */}
          <div className="absolute bottom-2 right-2">
            {hasDiscount && tour.original_price ? (
              <div className="flex flex-col items-end">
                <span className="text-xs text-white/80 line-through">{formatPrice(tour.original_price)}</span>
                <span className="bg-secondary text-secondary-foreground px-3 py-1 rounded-full font-bold text-sm">
                  {formatPrice(tour.price)}
                </span>
              </div>
            ) : (
              <span className="bg-secondary text-secondary-foreground px-3 py-1 rounded-full font-bold text-sm">
                {formatPrice(tour.price)}
              </span>
            )}
          </div>
        </div>

        {/* Content - More padding and bigger text */}
        <div className="p-4">
          <h3 className="font-heading text-base md:text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
            {tour.title}
          </h3>

          {/* Discount Label */}
          {tour.discount_label && (
            <div className="inline-flex items-center gap-1 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 px-2 py-1 rounded-full text-xs font-medium mb-2">
              <span>{tour.discount_label}</span>
            </div>
          )}

          {/* Urgency indicator */}
          {spotsLeft <= 4 && showSpotsLeft && !tour.discount_label && (
            <div className="flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-1 rounded-full text-xs font-medium mb-2 w-fit">
              <Users className="h-3 w-3" />
              <span>Only {spotsLeft} spots left</span>
            </div>
          )}

          <div className="flex items-center text-xs text-muted-foreground">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>{tour.duration}</span>
              </div>
              <div className="flex items-center gap-1 text-green-600 font-medium">
                <Check className="h-3 w-3" />
                <span>Free Cancel</span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
};

// Search Dropdown Portal Component
const SearchDropdownPortal = ({ children, show }) => {
  if (!show) return null;
  return createPortal(children, document.body);
};

// Search Bar with Suggestions Component
const SearchBar = ({ onSearch, onFocusChange }) => {
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState(defaultSearchSuggestions);
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const handleClose = () => {
    setShowSuggestions(false);
    setQuery('');
    onFocusChange?.(false);
    inputRef.current?.blur();
  };

  // Calculate dropdown position to show below input but within viewport
  const updateDropdownPosition = () => {
    if (searchRef.current) {
      const rect = searchRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      // Calculate available space below input
      const spaceBelow = viewportHeight - rect.bottom - 16;
      
      // Set a good height for showing ~6-7 items visible, rest scrollable
      // Each item is ~60px, header is ~52px, so ~450px shows about 6-7 items
      const desiredHeight = 450;
      const maxHeight = Math.min(desiredHeight, spaceBelow);
      
      // Calculate left position ensuring dropdown doesn't overflow viewport
      let left = rect.left;
      let width = rect.width;
      
      // On mobile, make it full width with some padding
      if (viewportWidth <= 500) {
        left = 16;
        width = viewportWidth - 32;
      }
      
      setDropdownStyle({
        position: 'fixed',
        top: `${rect.bottom + 8}px`,
        left: `${left}px`,
        width: `${width}px`,
        maxHeight: `${maxHeight}px`,
        zIndex: 10000,
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.querySelector('[data-testid="search-suggestions"]');
      const backdrop = document.querySelector('[data-testid="search-backdrop"]');
      
      // Close if clicking on backdrop
      if (backdrop && backdrop.contains(event.target)) {
        setShowSuggestions(false);
        onFocusChange?.(false);
        return;
      }
      
      // Don't close if clicking inside search or dropdown
      if (searchRef.current && searchRef.current.contains(event.target)) return;
      if (dropdown && dropdown.contains(event.target)) return;
      
      setShowSuggestions(false);
      onFocusChange?.(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onFocusChange]);

  // Update position on scroll and resize
  useEffect(() => {
    if (showSuggestions) {
      updateDropdownPosition();
      const handleUpdate = () => updateDropdownPosition();
      window.addEventListener('scroll', handleUpdate, true);
      window.addEventListener('resize', handleUpdate);
      return () => {
        window.removeEventListener('scroll', handleUpdate, true);
        window.removeEventListener('resize', handleUpdate);
      };
    }
  }, [showSuggestions]);

  // Fetch product suggestions when query changes
  useEffect(() => {
    const fetchProductSuggestions = async () => {
      if (query.trim().length >= 2) {
        setLoadingProducts(true);
        try {
          const response = await toursAPI.searchSuggestions(query);
          setProductSuggestions(response.data || []);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          setProductSuggestions([]);
        } finally {
          setLoadingProducts(false);
        }
      } else {
        setProductSuggestions([]);
      }
    };

    const debounceTimer = setTimeout(fetchProductSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [query]);

  useEffect(() => {
    if (query.trim()) {
      const filtered = defaultSearchSuggestions.filter(s => 
        s.query.toLowerCase().includes(query.toLowerCase()) ||
        (s.category && s.category.toLowerCase().includes(query.toLowerCase()))
      );
      setFilteredSuggestions(filtered.length > 0 ? filtered : defaultSearchSuggestions);
    } else {
      setFilteredSuggestions(defaultSearchSuggestions);
    }
  }, [query]);

  const handleSearch = (searchQuery) => {
    navigate(`/tours?search=${encodeURIComponent(searchQuery)}`);
    setShowSuggestions(false);
    onFocusChange?.(false);
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.category) {
      navigate(`/tours?category=${suggestion.category}&search=${encodeURIComponent(suggestion.query)}`);
    } else {
      navigate(`/tours?search=${encodeURIComponent(suggestion.query)}`);
    }
    setShowSuggestions(false);
    onFocusChange?.(false);
  };

  const handleProductClick = (product) => {
    navigate(`/tours/${product.id}`);
    setShowSuggestions(false);
    onFocusChange?.(false);
  };

  return (
    <>
      {/* Portal for backdrop and dropdown - renders at body level to escape all stacking contexts */}
      <SearchDropdownPortal show={showSuggestions}>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/40"
          style={{ zIndex: 9998 }}
          onClick={handleClose}
          data-testid="search-backdrop"
        />
        
        {/* Suggestions Dropdown */}
        <div 
          className="bg-white rounded-2xl shadow-2xl border border-border overflow-hidden"
          style={dropdownStyle}
          data-testid="search-suggestions"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-border bg-gray-50 sticky top-0">
            <span className="text-sm font-semibold text-foreground">
              {query.length >= 2 && productSuggestions.length > 0 ? 'Matching Tours' : 'Popular Searches'}
            </span>
            <button
              onClick={handleClose}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              data-testid="suggestions-close-btn"
            >
              <X className="h-4 w-4" />
              Close
            </button>
          </div>
          
          {/* Suggestions List */}
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(100% - 52px)' }}>
            {/* Product Results (when typing) */}
            {query.length >= 2 && productSuggestions.length > 0 && (
              <>
                {productSuggestions.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => handleProductClick(product)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left border-b border-border/50 last:border-0"
                    data-testid={`product-suggestion-${product.id}`}
                  >
                    <img 
                      src={product.image_url} 
                      alt={product.title}
                      className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{product.title}</p>
                      <p className="text-xs text-muted-foreground capitalize">{product.category} Tour • ${product.price}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </button>
                ))}
                <div className="p-3 border-t bg-gray-50">
                  <button
                    onClick={() => handleSearch(query)}
                    className="w-full text-center text-sm text-primary font-medium hover:underline"
                  >
                    See all results for "{query}"
                  </button>
                </div>
              </>
            )}

            {/* Loading state */}
            {loadingProducts && query.length >= 2 && (
              <div className="p-4 text-center text-muted-foreground">
                <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2" />
                <p className="text-sm">Searching tours...</p>
              </div>
            )}

            {/* No results message */}
            {query.length >= 2 && !loadingProducts && productSuggestions.length === 0 && (
              <div className="p-4 text-center text-muted-foreground">
                <p className="text-sm">No tours found for "{query}"</p>
                <button
                  onClick={() => handleSearch(query)}
                  className="mt-2 text-sm text-primary font-medium hover:underline"
                >
                  Search anyway
                </button>
              </div>
            )}

            {/* Default suggestions (when not typing enough) */}
            {query.length < 2 && filteredSuggestions.map((suggestion, index) => {
              const IconComponent = suggestion.icon || Search;
              return (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left border-b border-border/50 last:border-0"
                data-testid={`suggestion-${suggestion.query.toLowerCase().replace(' ', '-')}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  suggestion.category === 'adventure' ? 'bg-tropical-green/10 text-tropical-green' :
                  suggestion.category === 'marine' ? 'bg-tropical-turquoise/10 text-tropical-turquoise' :
                  suggestion.category === 'food' ? 'bg-orange-500/10 text-orange-500' :
                  'bg-purple-600/10 text-purple-600'
                }`}>
                  <IconComponent className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground">{suggestion.query}</p>
                  <p className="text-xs text-muted-foreground">{suggestion.description || suggestion.category}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </button>
            );
            })}
          </div>
        </div>
      </SearchDropdownPortal>
      
      <div ref={searchRef} className="relative w-full max-w-2xl" data-testid="search-bar">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Search Tours ..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => {
              setShowSuggestions(true);
              setTimeout(updateDropdownPosition, 10);
              onFocusChange?.(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && query.trim()) {
                handleSearch(query);
              }
              if (e.key === 'Escape') {
                handleClose();
              }
            }}
            className="w-full pl-12 pr-28 py-6 text-base rounded-full border-2 border-white/30 bg-white shadow-lg focus:border-secondary focus:ring-secondary"
            data-testid="search-input"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {showSuggestions && (
              <Button 
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200"
                data-testid="search-close-btn"
              >
                <X className="h-5 w-5 text-gray-600" />
              </Button>
            )}
            <Button 
              onClick={() => query.trim() && handleSearch(query)}
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-6"
              data-testid="search-btn"
            >
              Search
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default function HomePage() {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchFocused, setSearchFocused] = useState(false);
  const [quickSearches, setQuickSearches] = useState(defaultSearchSuggestions);
  const [categories, setCategories] = useState(fallbackCategories);
  const [listingSettings, setListingSettings] = useState({
    layout: 'grid-4',
    cardSize: 'standard',
    showPrices: true,
    showRatings: true,
    showBadges: true,
    cardBorderRadius: 12,
    cardShadow: 'medium',
    priceColor: '#22c55e',
    badgePosition: 'top-left',
    hoverEffect: 'scale',
  });
  const { isAuthenticated } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        await seedAPI.seed();
        const [toursResponse, searchesResponse, categoriesResponse] = await Promise.all([
          toursAPI.getAll({}),
          quickSearchesAPI.getAll().catch(() => ({ data: [] })),
          categoriesAPI.getAll().catch(() => ({ data: [] }))
        ]);
        setTours(toursResponse.data);
        if (searchesResponse.data && searchesResponse.data.length > 0) {
          setQuickSearches(searchesResponse.data.map(s => ({
            query: s.query,
            name: s.name,
            category: s.category
          })));
        }
        if (categoriesResponse.data && categoriesResponse.data.length > 0) {
          setCategories(categoriesResponse.data);
        }
        
        // Load listing settings
        try {
          const settingsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`);
          const settingsData = await settingsRes.json();
          if (settingsData?.listing_settings) {
            setListingSettings(prev => ({ ...prev, ...settingsData.listing_settings }));
          }
        } catch (e) {
          // Using default listing settings - this is expected for new setups
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Get colors from settings
  const homeBgColor = settings?.home_bg_color || '#ffffff';
  const heroBgColor = settings?.hero_bg_color || '#166534';

  return (
    <div className="min-h-screen" style={{ backgroundColor: homeBgColor }}>
      <Navbar />
      
      {/* Featured Tours Carousel */}
      <section className="py-6" style={{ backgroundColor: `${homeBgColor}ee` }} data-testid="featured-carousel-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FeaturedToursCarousel />
        </div>
      </section>
      
      {/* Search Section - Compact */}
      <section className="relative py-6" style={{ backgroundColor: heroBgColor }} data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            {/* Search Bar */}
            <SearchBar onFocusChange={setSearchFocused} />
          </div>
        </div>
      </section>

      {/* Quick Search Tags */}
      <section className="py-4 bg-muted/30" data-testid="quick-tags">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <span className="text-sm text-muted-foreground mr-2">Quick searches:</span>
            {quickSearches.slice(0, 6).map((tag, index) => (
              <Link 
                key={index} 
                to={tag.category ? `/tours?category=${tag.category}` : `/tours?search=${encodeURIComponent(tag.query)}`}
                className="px-3 py-1.5 bg-white border border-border rounded-full text-sm hover:bg-primary hover:text-white hover:border-primary transition-colors"
                data-testid={`quick-tag-${(tag.name || tag.query).toLowerCase().replace(' ', '-')}`}
              >
                {tag.name || tag.query}
              </Link>
            ))}
          </div>
        </div>
      </section>
      
      {/* Trust Badges */}
      <TrustBadges />

      {/* Popular Tours - Horizontal Layout */}
      <section className="py-10 bg-background" data-testid="featured-tours-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground">
                Popular Tours
              </h2>
              <p className="text-muted-foreground text-sm mt-1">
                Most booked experiences in Puerto Rico
              </p>
            </div>
            <Link to="/tours">
              <Button variant="outline" className="rounded-full gap-1" data-testid="view-all-btn">
                View All
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className={`grid gap-5 ${
              listingSettings.layout === 'grid-2' ? 'grid-cols-1 md:grid-cols-2' :
              listingSettings.layout === 'grid-3' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' :
              'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            }`}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="skeleton rounded-xl h-[180px]" />
              ))}
            </div>
          ) : (
            <div 
              className={`grid gap-5 ${
                listingSettings.layout === 'grid-2' ? 'grid-cols-1 md:grid-cols-2' :
                listingSettings.layout === 'grid-3' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' :
                'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
              }`} 
              data-testid="tours-grid"
            >
              {tours.slice(0, 12).map((tour) => (
                <HorizontalTourCard 
                  key={tour.id} 
                  tour={tour} 
                  listingSettings={listingSettings}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Category Showcase - Smaller Cards */}
      <section className="py-12 bg-muted/30" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground">
              Browse by Category
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((category) => {
              // Get icon component from map or fallback to Compass
              const IconComponent = iconMap[category.icon] || Compass;
              const tourCount = tours.filter(t => t.category === category.slug).length;
              
              // Use category image from API or fallback to defaults
              const defaultImages = {
                adventure: 'https://images.unsplash.com/photo-1660248602942-131a16b3e8a1?w=800',
                marine: 'https://images.unsplash.com/photo-1599943503164-2dcaab73121a?w=800',
                food: 'https://images.pexels.com/photos/16215562/pexels-photo-16215562.jpeg?w=800',
                night: 'https://images.unsplash.com/photo-1554848863-e7a1238a8bb0?w=800',
              };
              const image = category.image_url || defaultImages[category.slug] || 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800';
              
              return (
                <Link 
                  key={category.id || category.slug} 
                  to={`/tours?category=${category.slug}`}
                  data-testid={`category-${category.slug}`}
                >
                  <Card className="category-card group relative aspect-[4/3] rounded-xl overflow-hidden cursor-pointer">
                    <img 
                      src={image} 
                      alt={category.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute inset-0 z-10 flex flex-col justify-end p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <IconComponent className="h-4 w-4 text-white" />
                        <h3 className="font-heading text-base font-semibold text-white">
                          {category.name}
                        </h3>
                      </div>
                      <p className="text-white/80 text-xs">
                        {tourCount} tours available
                      </p>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features - Compact */}
      <section className="py-12 bg-background" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 p-4 bg-white rounded-xl border border-border"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-sm">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-xs">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Compact */}
      <section className="py-12 bg-primary" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4">
            Ready to Explore Puerto Rico?
          </h2>
          <Link to="/tours">
            <Button 
              size="lg" 
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-8"
            >
              Start Exploring
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
